import { PageHeader } from '@ant-design/pro-layout';
import { Button, Col, DatePicker, Form, Grid, Input, message, Modal, Row, Select } from 'antd';
import Title from 'antd/es/typography/Title';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { GENDERS } from '../../../../constants';
import '../../../../css/entity-style.css';
import { PhotoUpload } from '../../../shared/photo-upload/photo-upload';
import { FormatDate, DATE_FORMAT } from '../../../../constants';


interface IdFormProps {
  generatePdf: (formData: IdFormData) => Promise<File>;
}

export interface IdFormData {
  caseId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  photo: string;
  expirationPhoto: string;
}

export const IdForm: React.FC<IdFormProps> = (props) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;
  const [form] = Form.useForm<IdFormData>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');

  const handleModalClose = () => {
    setIsModalVisible(false);
    form.resetFields();
    URL.revokeObjectURL(pdfUrl);
  };

  const onSubmit = async (values: any) => {
    try {
      const photoFile = values.photo.originFileObj;
      const expirationFile = values.expirationPhoto.originFileObj;
      const readFile = (file: File) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });
      const photoData = await readFile(photoFile);
      const expirationData = await readFile(expirationFile);

      const idFormObj: IdFormData = {
        caseId: values.caseId,
        firstName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: FormatDate(values.dateOfBirth),
        gender: values.gender,
        photo: photoData,
        expirationPhoto: expirationData
      };
      localStorage.setItem('idFormData', JSON.stringify(idFormObj));
      message.success('Form submitted successfully!');
      const generatedPdf = await props.generatePdf(idFormObj);
      const pdfBlob = new Blob([generatedPdf], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(pdfUrl);
      setIsModalVisible(true);
    } catch (error) {
      message.error('An error occurred while submitting the form.');
      console.error(error);
    }
  };

  return (
    <>
      <PageHeader>
        <div className="flex flex-col" style={{ marginBottom: '30px' }}>
          <div id="leftTitle" style={{ display: 'inline', float: 'left' }}>
            <Title level={3} style={{ display: 'inline' }}>
              ID Form
            </Title>
          </div>
        </div>
      </PageHeader>
      <div className="flex flex-col">
        <div className="flex justify-between items-center">
          <Form
            form={form}
            onFinish={(values) => {
              onSubmit(values);
            }}
            onFinishFailed={(err) => {
              console.log('err', err);
            }}
            layout="vertical"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'right',
              justifyContent: 'end'
            }}
          >
            <Row gutter={20}>
              <Col xs={24} sm={25}>
                <Form.Item label="Case ID" name="caseId" rules={[{ min: 24, message: 'Case ID must be at least 24 characters long' }]}>
                  <Input placeholder="Enter Case ID" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: 'First Name is required' }]}>
                  <Input placeholder="Enter First Name" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: 'Last Name is required' }]}>
                  <Input placeholder="Enter Last Name" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Date Of Birth"
                  style={{ width: '100%' }}
                  name="dateOfBirth"
                  valuePropName="value"
                  rules={[{ required: true, message: 'Date Of Birth is Required' }]}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : ''
                  })}
                >
                  <DatePicker maxDate={dayjs().endOf('day')} format={(value) => value.format(DATE_FORMAT).toUpperCase()} style={{ width: isMobile ? '100%' : 'auto' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="gender" rules={[{ required: true, message: 'Gender is required' }]} label="Gender">
                  <Select style={{ width: isMobile ? '100%' : 100 }} placeholder="Gender">
                    {GENDERS.map((item) => {
                      return (
                        <Select.Option key={item.value} value={item.value} style={{ width: isMobile ? '100%' : 100, minHeight: '40px', padding: '8px' }}>
                          {item.label}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col xs={24} sm={11}>
                <PhotoUpload name="photo" label="Photo" form={form} cropperProps={{ aspect: 1 }} />
              </Col>
              <Col xs={24} sm={13}>
                <PhotoUpload name="expirationPhoto" label="Passport" form={form} cropperProps={{ aspect: 3 / 4 }} />
              </Col>
            </Row>
            <Row gutter={20}>
              <Col xs={24} sm={50}>
                <Form.Item>
                  <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
                    Generate ID Form
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
        <div>
          <Modal open={isModalVisible} footer={null} onCancel={handleModalClose} width="80%" height="150%" style={{ top: 20 }}>
            <embed src={pdfUrl ?? undefined} type="application/pdf" width="100%" height="1000px" title="Generated PDF" />
          </Modal>
        </div>
      </div>
    </>
  );
};
